import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import DocumentDownloadButton from '../components/DocumentDownloadButton'
import { hasSignedDocument, signedDocumentUnavailable } from '../lib/documentDownloads'

const ENTITY_LABELS = { '1040': '1040 — Personal', '1120S': '1120S — S-Corp', '1065': '1065 — Partnership', '1120': '1120 — C-Corp', 'schedule_c': 'Sch C — Sole Prop', '990': '990 — Nonprofit' }
const STATUSES = ['not_started','in_progress','review','filed','on_hold']
const STATUS_LABELS = { not_started:'Not Started', in_progress:'In Progress', review:'In Review', filed:'Filed', on_hold:'On Hold' }
const STATUS_COLORS = { not_started:'#62615D', in_progress:'#3B82F6', review:'#F59E0B', filed:'#10B981', on_hold:'#EF4444' }
const DOC_STATUS_COLORS = { pending:'#F59E0B', reviewed:'#10B981', needs_resubmission:'#EF4444' }
const INV_STATUS_COLORS = { draft:'#62615D', sent:'#3B82F6', paid:'#10B981', void:'#9CA3AF', overdue:'#EF4444' }

function Badge({ label, color }) {
  return <span style={{ background: color + '20', color, border: `1px solid ${color}40`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</span>
}

function Tab({ label, active, onClick, count }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 18px', border: 'none', borderBottom: active ? '2px solid #CBB17B' : '2px solid transparent',
      background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: active ? 700 : 500,
      color: active ? '#202326' : '#62615D', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap'
    }}>
      {label}
      {count !== undefined && count > 0 && (
        <span style={{ background: active ? '#CBB17B' : '#DED6CA', color: active ? '#202326' : '#62615D', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{count}</span>
      )}
    </button>
  )
}

export default function ClientProfilePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [client, setClient] = useState(null)
  const [documents, setDocuments] = useState([])
  const [invoices, setInvoices] = useState([])
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('overview')
  const [toast, setToast] = useState('')
  const [toastType, setToastType] = useState('success')
  const [inviteLoading, setInviteLoading] = useState(false)
  const [notes, setNotes] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [sendingMsg, setSendingMsg] = useState(false)
  const [editingStatus, setEditingStatus] = useState(false)
  const [newInvoiceModal, setNewInvoiceModal] = useState(false)
  const [invoiceForm, setInvoiceForm] = useState({ amount: '', description: '', due_date: '' })
  const [creatingInvoice, setCreatingInvoice] = useState(false)
  const [generatingLink, setGeneratingLink] = useState(null)
  const [taxProfile, setTaxProfile] = useState({})
  const [savingProfile, setSavingProfile] = useState(false)
  const messagesEndRef = useRef(null)
  const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC']
  const FILING_STATUSES = ['Single','Married Filing Jointly','Married Filing Separately','Head of Household','Qualifying Widow(er)']

  const notify = (msg, type = 'success') => { setToast(msg); setToastType(type); setTimeout(() => setToast(''), 4000) }

  useEffect(() => { load() }, [id])

  useEffect(() => {
    if (tab === 'messages') messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, tab])

  async function load() {
    const { data: c } = await supabase.from('clients').select('*').eq('id', id).single()
    if (!c || c.cpa_id !== profile.id) { navigate('/clients'); return }
    setClient(c)
    setNotes(c.notes || '')

    const [docsRes, invRes, msgRes] = await Promise.all([
      supabase.from('documents').select('*').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('invoices').select('*').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('messages').select('*').eq('client_id', id).order('created_at', { ascending: true }),
    ])
    setDocuments(docsRes.data || [])
    setInvoices(invRes.data || [])
    setMessages(msgRes.data || [])
    const { data: tp } = await supabase.from('client_profiles').select('*').eq('client_id', id).single()
    setTaxProfile(tp || {})
    setLoading(false)
  }

  async function handleStatusChange(status) {
    const { data } = await supabase.from('clients').update({ status, updated_at: new Date().toISOString() }).eq('id', id).select().single()
    if (data) { setClient(data); notify('Status updated') }
    setEditingStatus(false)
  }

  async function handleSendInvite() {
    setInviteLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/invite-client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ email: client.email, client_id: client.id, full_name: client.full_name })
      })
      const data = await res.json()
      if (data.error) notify(data.error, 'error')
      else if (data.emailSent === false) {
        notify('Client created, but welcome email FAILED. Share credentials manually.', 'error')
      } else {
        notify(data.wasPasswordReset ? 'Password reset and email sent to client.' : 'Client created and welcome email sent.')
      }
    } catch { notify('Failed to send invite', 'error') }
    setInviteLoading(false)
  }

  async function handleSaveNotes() {
    setSavingNotes(true)
    await supabase.from('clients').update({ notes, updated_at: new Date().toISOString() }).eq('id', id)
    notify('Notes saved')
    setSavingNotes(false)
  }

  async function handleSaveProfile() {
    setSavingProfile(true)
    const payload = { ...taxProfile, client_id: id, updated_at: new Date().toISOString() }
    const { data: existing } = await supabase.from('client_profiles').select('id').eq('client_id', id).maybeSingle()
    if (existing) {
      await supabase.from('client_profiles').update(payload).eq('client_id', id)
    } else {
      await supabase.from('client_profiles').insert(payload)
    }
    notify('Tax profile saved!')
    setSavingProfile(false)
  }

  async function handleMarkDocument(docId, status) {
    const { data } = await supabase.from('documents').update({ status, updated_at: new Date().toISOString() }).eq('id', docId).select().single()
    if (data) setDocuments(prev => prev.map(d => d.id === docId ? data : d))
    notify(`Document marked as ${status.replace('_', ' ')}`)
  }

  async function handleViewDocument(doc) {
    const { data, error } = await supabase.storage.from('client-documents').createSignedUrl(doc.file_path, 300)
    if (error || !data?.signedUrl) {
      notify('Could not open document')
      return
    }

    window.open(data.signedUrl, '_blank')

    try {
      await supabase.from('activity_log').insert({
        client_id: doc.client_id || id,
        actor_id: profile?.id || null,
        action: 'document_downloaded',
        details: {
          file_name: doc.file_name,
          client_name: client?.business_name || client?.full_name || '',
          performed_by_email: profile?.email,
          source: 'client_profile',
        },
      })
    } catch {}
  }

  async function handleSendMessage() {
    if (!newMessage.trim()) return
    setSendingMsg(true)
    const { data } = await supabase.from('messages').insert({
      client_id: id, cpa_id: profile.id,
      sender: 'cpa', sender_role: 'cpa',
      body: newMessage.trim(), content: newMessage.trim(), read: false
    }).select().single()
    if (data) { setMessages(prev => [...prev, data]); setNewMessage('') }
    setSendingMsg(false)
  }

  async function handleCreateInvoice() {
    if (!invoiceForm.amount || !invoiceForm.description) return
    setCreatingInvoice(true)
    const { data, error } = await supabase.from('invoices').insert({
      client_id: id, cpa_id: profile.id,
      total_amount: parseFloat(invoiceForm.amount),
      description: invoiceForm.description,
      due_date: invoiceForm.due_date || null,
      status: 'draft',
      invoice_number: 'CF-' + Math.floor(100000 + Math.random() * 900000),
    }).select().single()
    if (data) {
      setInvoices(prev => [data, ...prev])
      setInvoiceForm({ amount: '', description: '', due_date: '' })
      setNewInvoiceModal(false)
      notify('Invoice created')
    } else notify(error?.message || 'Failed to create invoice', 'error')
    setCreatingInvoice(false)
  }

  async function handleGeneratePaymentLink(invoice) {
    setGeneratingLink(invoice.id)
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session?.access_token) throw new Error('Your session has expired. Please sign in again.')
      const res = await fetch('/.netlify/functions/create-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          amount: invoice.total_amount,
          invoice_id: invoice.id,
          invoice_number: invoice.invoice_number,
          client_email: client.email,
          description: invoice.description || `Invoice ${invoice.invoice_number}`,
        })
      })
      const data = await res.json()
      if (data.url) {
        setInvoices(prev => prev.map(i => i.id === invoice.id ? { ...i, stripe_payment_link: data.url, status: 'sent' } : i))
        notify('Payment link generated and invoice marked as sent')
      } else notify(data.error || 'Failed to generate link', 'error')
    } catch (err) { notify(err.message || 'Failed to generate payment link', 'error') }
    setGeneratingLink(null)
  }

  async function handleMarkInvoicePaid(invoiceId) {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch('/.netlify/functions/mark-invoice-paid', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
      body: JSON.stringify({ invoice_id: invoiceId })
    })
    const data = await res.json()
    if (data.success) {
      setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, status: 'paid', paid_at: new Date().toISOString() } : i))
      notify('Invoice marked as paid')
    } else notify(data.error || 'Failed', 'error')
  }

  if (loading) return (
    <div>{[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 100, marginBottom: 14 }} />)}</div>
  )

  const pendingDocs = documents.filter(d => d.status === 'pending').length
  const unpaidInvoices = invoices.filter(i => !['paid', 'void'].includes(i.status)).length
  const unreadMsgs = messages.filter(m => m.sender === 'client' && !m.read).length
  const daysUntilDeadline = client.deadline
    ? Math.ceil((new Date(client.deadline) - new Date()) / (1000 * 60 * 60 * 24))
    : null

  return (
    <div style={{ maxWidth: 860 }}>
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: toastType === 'error' ? '#FEE2E2' : '#202326', color: toastType === 'error' ? '#991B1B' : '#CBB17B', padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700, zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>{toast}</div>
      )}

      <div className="card" style={{ padding: '20px 24px', marginBottom: 16, background: '#202326', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/clients')} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}>← Clients</button>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(201,168,76,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#CBB17B', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
            {(client.business_name || client.full_name).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 2 }}>{client.business_name || client.full_name}</h2>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
              {client.full_name} · {ENTITY_LABELS[client.entity_type]} · Tax Year {client.tax_year}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {editingStatus ? (
              <select autoFocus className="input" style={{ fontSize: 12, padding: '6px 10px', width: 150 }} value={client.status} onChange={e => handleStatusChange(e.target.value)} onBlur={() => setEditingStatus(false)}>
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            ) : (
              <button onClick={() => setEditingStatus(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <Badge label={STATUS_LABELS[client.status]} color={STATUS_COLORS[client.status]} />
              </button>
            )}
            <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={handleSendInvite} disabled={inviteLoading}>
              {inviteLoading ? 'Sending…' : client.portal_user_id ? '📧 Resend Invite' : '📧 Send Invite'}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: '14px 20px', marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Email', value: client.email },
          { label: 'Phone', value: client.phone || '—' },
          { label: 'TIN / EIN', value: client.taxpayer_id || '—' },
          { label: 'Address', value: [client.street_address, client.city, client.state, client.zip_code].filter(Boolean).join(', ') || '—' },
          { label: 'Deadline', value: client.deadline ? new Date(client.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—', alert: daysUntilDeadline !== null && daysUntilDeadline <= 14 },
          { label: 'Portal', value: client.portal_user_id ? '✅ Active' : '⏳ Not invited' },
        ].map(({ label, value, alert }) => (
          <div key={label}>
            <div style={{ fontSize: 11, color: '#62615D', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: alert ? '#EF4444' : '#202326' }}>{value || '—'}</div>
          </div>
        ))}
      </div>

      {daysUntilDeadline !== null && daysUntilDeadline <= 14 && daysUntilDeadline >= 0 && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 10, padding: '10px 16px', fontSize: 13, marginBottom: 16, fontWeight: 600 }}>
          ⚠️ Filing deadline in {daysUntilDeadline} day{daysUntilDeadline !== 1 ? 's' : ''} — {new Date(client.deadline).toLocaleDateString()}
        </div>
      )}
      {daysUntilDeadline !== null && daysUntilDeadline < 0 && (
        <div style={{ background: '#FEE2E2', border: '1px solid #F87171', borderRadius: 10, padding: '10px 16px', fontSize: 13, marginBottom: 16, fontWeight: 600 }}>
          🚨 Deadline passed {Math.abs(daysUntilDeadline)} days ago
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', overflowX: 'auto', paddingLeft: 8 }}>
          <Tab label="Overview" active={tab === 'overview'} onClick={() => setTab('overview')} />
          <Tab label="Documents" active={tab === 'documents'} onClick={() => setTab('documents')} count={pendingDocs} />
          <Tab label="Payments" active={tab === 'invoices'} onClick={() => setTab('invoices')} count={unpaidInvoices} />
          <Tab label="Messages" active={tab === 'messages'} onClick={() => setTab('messages')} count={unreadMsgs} />
          <Tab label="Notes" active={tab === 'notes'} onClick={() => setTab('notes')} />
          <Tab label="Tax Profile" active={tab === 'taxprofile'} onClick={() => setTab('taxprofile')} />
        </div>

        <div style={{ padding: 20 }}>

          {tab === 'overview' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Documents', value: documents.length, sub: `${pendingDocs} pending`, color: '#3B82F6', onClick: () => setTab('documents') },
                  { label: 'Invoices', value: invoices.length, sub: `${unpaidInvoices} unpaid`, color: '#10B981', onClick: () => setTab('invoices') },
                  { label: 'Messages', value: messages.length, sub: `${unreadMsgs} unread`, color: '#8B5CF6', onClick: () => setTab('messages') },
                  { label: 'Outstanding', value: '$' + invoices.filter(i => !['paid','void'].includes(i.status)).reduce((a, i) => a + parseFloat(i.total_amount || 0), 0).toFixed(2), sub: 'balance due', color: '#F59E0B' },
                ].map(({ label, value, sub, color, onClick }) => (
                  <div key={label} className="card" onClick={onClick} style={{ padding: '16px 18px', cursor: onClick ? 'pointer' : 'default', border: `1px solid ${color}30`, background: color + '08' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#202326', marginTop: 2 }}>{label}</div>
                    <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>{sub}</div>
                  </div>
                ))}
              </div>
              {documents.slice(0, 3).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#62615D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Recent Documents</div>
                  {documents.slice(0, 3).map(d => (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 16 }}>📄</span>
                      <span style={{ flex: 1, fontSize: 13, color: '#202326' }}>{d.file_name.replace(/^\d+_/, '')}</span>
                      <Badge label={d.status.replace('_', ' ')} color={DOC_STATUS_COLORS[d.status] || '#62615D'} />
                    </div>
                  ))}
                </div>
              )}
              {invoices.slice(0, 3).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#62615D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Recent Invoices</div>
                  {invoices.slice(0, 3).map(inv => (
                    <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 16 }}>🧾</span>
                      <span style={{ flex: 1, fontSize: 13, color: '#202326' }}>{inv.invoice_number} — {inv.description || 'Tax Services'}</span>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>${parseFloat(inv.total_amount || 0).toFixed(2)}</span>
                      <Badge label={inv.status} color={INV_STATUS_COLORS[inv.status] || '#62615D'} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'documents' && (
            <div>
              {documents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#62615D' }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>📁</div>
                  <div style={{ fontSize: 14 }}>No documents uploaded yet</div>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {documents.map(doc => (
                    <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#F7F3EB', borderRadius: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 20 }}>📄</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          onClick={() => handleViewDocument(doc)}
                          title="View original document"
                          style={{ fontSize: 13, fontWeight: 600, color: '#202326', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#CBB17B40', textUnderlineOffset: 3 }}
                        >
                          {doc.file_name.replace(/^\d+_/, '')}
                        </div>
                        <div style={{ fontSize: 11, color: '#62615D', marginTop: 2 }}>
                          {doc.category || 'General'} · {doc.tax_year || client.tax_year} · {(doc.file_size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      {hasSignedDocument(doc) && <DocumentDownloadButton doc={doc} className="btn btn-primary" style={{ fontSize: 11, padding: '6px 10px' }} />}
                      <DocumentDownloadButton doc={doc} variant="original" style={{ fontSize: 11, padding: '6px 10px' }} />
                      <button onClick={() => handleViewDocument(doc)} style={{ fontSize: 11, padding: '4px 10px', background: '#3B82F620', color: '#3B82F6', border: '1px solid #3B82F640', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>👁 {doc.requires_signature || hasSignedDocument(doc) || signedDocumentUnavailable(doc) ? 'View original' : 'View'}</button>
                      {signedDocumentUnavailable(doc) && <span style={{ fontSize: 12, color: '#92400E' }}>Signed PDF unavailable</span>}
                      <Badge label={doc.status.replace('_', ' ')} color={DOC_STATUS_COLORS[doc.status] || '#62615D'} />
                      {doc.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleMarkDocument(doc.id, 'reviewed')} style={{ fontSize: 11, padding: '4px 10px', background: '#10B98120', color: '#10B981', border: '1px solid #10B98140', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>✓ Reviewed</button>
                          <button onClick={() => handleMarkDocument(doc.id, 'needs_resubmission')} style={{ fontSize: 11, padding: '4px 10px', background: '#EF444420', color: '#EF4444', border: '1px solid #EF444440', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>↩ Resubmit</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'invoices' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => setNewInvoiceModal(true)}>+ New Invoice</button>
              </div>
              {newInvoiceModal && (
                <div style={{ background: '#F7F3EB', borderRadius: 12, padding: '18px 20px', marginBottom: 16, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>New Invoice</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    <div>
                      <label className="label">Amount ($) *</label>
                      <input className="input" type="number" placeholder="0.00" value={invoiceForm.amount} onChange={e => setInvoiceForm(f => ({ ...f, amount: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Due Date</label>
                      <input className="input" type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm(f => ({ ...f, due_date: e.target.value }))} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label className="label">Description *</label>
                      <input className="input" placeholder="e.g. 2024 Tax Preparation" value={invoiceForm.description} onChange={e => setInvoiceForm(f => ({ ...f, description: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setNewInvoiceModal(false)}>Cancel</button>
                    <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={handleCreateInvoice} disabled={creatingInvoice}>
                      {creatingInvoice ? 'Creating…' : 'Create Invoice'}
                    </button>
                  </div>
                </div>
              )}
              {invoices.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#62615D' }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🧾</div>
                  <div style={{ fontSize: 14 }}>No invoices yet</div>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {invoices.map(inv => (
                    <div key={inv.id} style={{ padding: '14px 16px', background: '#F7F3EB', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#202326' }}>{inv.invoice_number}</div>
                        <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>{inv.description || 'Tax Services'}{inv.due_date ? ` · Due ${new Date(inv.due_date).toLocaleDateString()}` : ''}</div>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#202326' }}>${parseFloat(inv.total_amount || 0).toFixed(2)}</div>
                      <Badge label={inv.status} color={INV_STATUS_COLORS[inv.status] || '#62615D'} />
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {inv.stripe_payment_link && (
                          <button onClick={() => navigator.clipboard.writeText(inv.stripe_payment_link).then(() => notify('Payment link copied!'))} style={{ fontSize: 11, padding: '4px 10px', background: '#3B82F620', color: '#3B82F6', border: '1px solid #3B82F640', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                            📋 Copy Link
                          </button>
                        )}
                        {!inv.stripe_payment_link && inv.status !== 'paid' && inv.status !== 'void' && (
                          <button onClick={() => handleGeneratePaymentLink(inv)} disabled={generatingLink === inv.id} style={{ fontSize: 11, padding: '4px 10px', background: '#CBB17B20', color: '#765B2C', border: '1px solid #CBB17B40', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                            {generatingLink === inv.id ? '…' : '🔗 Generate Link'}
                          </button>
                        )}
                        {inv.status !== 'paid' && inv.status !== 'void' && (
                          <button onClick={() => handleMarkInvoicePaid(inv.id)} style={{ fontSize: 11, padding: '4px 10px', background: '#10B98120', color: '#10B981', border: '1px solid #10B98140', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                            ✓ Mark Paid
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'messages' && (
            <div>
              <div style={{ maxHeight: 380, overflowY: 'auto', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {messages.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#62615D' }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>💬</div>
                    <div style={{ fontSize: 14 }}>No messages yet — start the conversation</div>
                  </div>
                ) : (
                  messages.map(msg => {
                    const isCpa = msg.sender === 'cpa'
                    return (
                      <div key={msg.id} style={{ display: 'flex', justifyContent: isCpa ? 'flex-end' : 'flex-start' }}>
                        <div style={{
                          maxWidth: '70%', padding: '10px 14px', borderRadius: isCpa ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                          background: isCpa ? '#202326' : '#F0ECE4', color: isCpa ? '#fff' : '#202326', fontSize: 13
                        }}>
                          <div>{msg.body || msg.content}</div>
                          <div style={{ fontSize: 10, marginTop: 4, opacity: 0.6, textAlign: isCpa ? 'right' : 'left' }}>
                            {new Date(msg.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="input" style={{ flex: 1 }} placeholder="Type a message…" value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage() } }} />
                <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={handleSendMessage} disabled={sendingMsg || !newMessage.trim()}>
                  {sendingMsg ? '…' : 'Send'}
                </button>
              </div>
            </div>
          )}

          {tab === 'notes' && (
            <div>
              <div style={{ fontSize: 12, color: '#62615D', marginBottom: 10 }}>Internal notes — not visible to the client</div>
              <textarea className="input" rows={10} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Prior year AGI, special circumstances, important dates, action items…"
                style={{ resize: 'vertical', width: '100%', fontFamily: 'inherit', lineHeight: 1.6 }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn btn-primary" onClick={handleSaveNotes} disabled={savingNotes}>
                  {savingNotes ? 'Saving…' : '💾 Save Notes'}
                </button>
              </div>
            </div>
          )}

          {tab === 'taxprofile' && (
            <div>
              <div style={{ fontSize: 12, color: '#62615D', marginBottom: 16 }}>Tax profile information — for internal use only</div>
              
              <div style={{ fontSize: 13, fontWeight: 700, color: '#202326', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Personal Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div><label className="label">First Name</label><input className="input" value={taxProfile.first_name || ''} onChange={e => setTaxProfile(p => ({...p, first_name: e.target.value}))} placeholder="John" /></div>
                <div><label className="label">Last Name</label><input className="input" value={taxProfile.last_name || ''} onChange={e => setTaxProfile(p => ({...p, last_name: e.target.value}))} placeholder="Smith" /></div>
                <div><label className="label">SSN</label><input className="input" type="password" value={taxProfile.ssn || ''} onChange={e => setTaxProfile(p => ({...p, ssn: e.target.value}))} placeholder="XXX-XX-XXXX" /></div>
                <div><label className="label">Date of Birth</label><input className="input" type="date" value={taxProfile.dob || ''} onChange={e => setTaxProfile(p => ({...p, dob: e.target.value}))} /></div>
                <div><label className="label">Filing Status</label>
                  <select className="input" value={taxProfile.filing_status || ''} onChange={e => setTaxProfile(p => ({...p, filing_status: e.target.value}))}>
                    <option value="">Select...</option>
                    {FILING_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div><label className="label">Occupation</label><input className="input" value={taxProfile.occupation || ''} onChange={e => setTaxProfile(p => ({...p, occupation: e.target.value}))} placeholder="e.g. Business Owner" /></div>
                <div><label className="label">Phone</label><input className="input" value={taxProfile.phone || ''} onChange={e => setTaxProfile(p => ({...p, phone: e.target.value}))} placeholder="(555) 123-4567" /></div>
                <div><label className="label">Alternate Phone</label><input className="input" value={taxProfile.alt_phone || ''} onChange={e => setTaxProfile(p => ({...p, alt_phone: e.target.value}))} placeholder="Optional" /></div>
                <div style={{ gridColumn: '1 / -1' }}><label className="label">Street Address</label><input className="input" value={taxProfile.address || ''} onChange={e => setTaxProfile(p => ({...p, address: e.target.value}))} placeholder="123 Main Street" /></div>
                <div><label className="label">City</label><input className="input" value={taxProfile.city || ''} onChange={e => setTaxProfile(p => ({...p, city: e.target.value}))} placeholder="City" /></div>
                <div><label className="label">State</label>
                  <select className="input" value={taxProfile.state || 'NC'} onChange={e => setTaxProfile(p => ({...p, state: e.target.value}))}>
                    {STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div><label className="label">ZIP</label><input className="input" value={taxProfile.zip || ''} onChange={e => setTaxProfile(p => ({...p, zip: e.target.value}))} placeholder="27870" /></div>
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, color: '#202326', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Spouse Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div><label className="label">Spouse First Name</label><input className="input" value={taxProfile.spouse_first_name || ''} onChange={e => setTaxProfile(p => ({...p, spouse_first_name: e.target.value}))} placeholder="Jane" /></div>
                <div><label className="label">Spouse Last Name</label><input className="input" value={taxProfile.spouse_last_name || ''} onChange={e => setTaxProfile(p => ({...p, spouse_last_name: e.target.value}))} placeholder="Smith" /></div>
                <div><label className="label">Spouse SSN</label><input className="input" type="password" value={taxProfile.spouse_ssn || ''} onChange={e => setTaxProfile(p => ({...p, spouse_ssn: e.target.value}))} placeholder="XXX-XX-XXXX" /></div>
                <div><label className="label">Spouse Date of Birth</label><input className="input" type="date" value={taxProfile.spouse_dob || ''} onChange={e => setTaxProfile(p => ({...p, spouse_dob: e.target.value}))} /></div>
                <div><label className="label">Spouse Occupation</label><input className="input" value={taxProfile.spouse_occupation || ''} onChange={e => setTaxProfile(p => ({...p, spouse_occupation: e.target.value}))} placeholder="e.g. Nurse" /></div>
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, color: '#202326', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Business Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div><label className="label">Business Name</label><input className="input" value={taxProfile.business_name || ''} onChange={e => setTaxProfile(p => ({...p, business_name: e.target.value}))} placeholder="Smith Enterprises LLC" /></div>
                <div><label className="label">EIN</label><input className="input" type="password" value={taxProfile.ein || ''} onChange={e => setTaxProfile(p => ({...p, ein: e.target.value}))} placeholder="XX-XXXXXXX" /></div>
                <div><label className="label">Business Type</label>
                  <select className="input" value={taxProfile.business_type || ''} onChange={e => setTaxProfile(p => ({...p, business_type: e.target.value}))}>
                    <option value="">Select...</option>
                    {['Sole Proprietorship','Partnership','S-Corporation','C-Corporation','LLC','Nonprofit'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div><label className="label">Date Started</label><input className="input" type="date" value={taxProfile.business_start_date || ''} onChange={e => setTaxProfile(p => ({...p, business_start_date: e.target.value}))} /></div>
                <div style={{ gridColumn: '1 / -1' }}><label className="label">Business Address</label><input className="input" value={taxProfile.business_address || ''} onChange={e => setTaxProfile(p => ({...p, business_address: e.target.value}))} placeholder="Business address" /></div>
                <div><label className="label">Business City</label><input className="input" value={taxProfile.business_city || ''} onChange={e => setTaxProfile(p => ({...p, business_city: e.target.value}))} /></div>
                <div><label className="label">Business State</label>
                  <select className="input" value={taxProfile.business_state || 'NC'} onChange={e => setTaxProfile(p => ({...p, business_state: e.target.value}))}>
                    {STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div><label className="label">Business ZIP</label><input className="input" value={taxProfile.business_zip || ''} onChange={e => setTaxProfile(p => ({...p, business_zip: e.target.value}))} /></div>
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, color: '#202326', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Prior Year Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div><label className="label">Prior Year AGI</label><input className="input" type="number" value={taxProfile.prior_agi || ''} onChange={e => setTaxProfile(p => ({...p, prior_agi: e.target.value}))} placeholder="0.00" /></div>
                <div><label className="label">Prior Year Refund / Balance Due</label><input className="input" value={taxProfile.prior_refund || ''} onChange={e => setTaxProfile(p => ({...p, prior_refund: e.target.value}))} placeholder="e.g. Refund $1,200" /></div>
                <div><label className="label">Prior Year Preparer</label><input className="input" value={taxProfile.prior_preparer || ''} onChange={e => setTaxProfile(p => ({...p, prior_preparer: e.target.value}))} placeholder="e.g. H&R Block" /></div>
                <div><label className="label">Years with your firm</label><input className="input" type="number" value={taxProfile.years_with_firm || ''} onChange={e => setTaxProfile(p => ({...p, years_with_firm: e.target.value}))} placeholder="1" /></div>
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, color: '#202326', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Dependents</div>
              <div style={{ marginBottom: 20 }}>
                {(taxProfile.dependents || []).map((dep, i) => (
                  <div key={i} style={{ background: '#F7F3EB', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 700 }}>Dependent {i + 1}</span>
                      <button onClick={() => setTaxProfile(p => ({...p, dependents: p.dependents.filter((_, j) => j !== i)}))} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div><label className="label">Full Name</label><input className="input" value={dep.name || ''} onChange={e => setTaxProfile(p => ({...p, dependents: p.dependents.map((d, j) => j === i ? {...d, name: e.target.value} : d)}))} /></div>
                      <div><label className="label">Relationship</label><input className="input" value={dep.relationship || ''} onChange={e => setTaxProfile(p => ({...p, dependents: p.dependents.map((d, j) => j === i ? {...d, relationship: e.target.value} : d)}))} placeholder="Son, Daughter, etc." /></div>
                      <div><label className="label">Date of Birth</label><input className="input" type="date" value={dep.dob || ''} onChange={e => setTaxProfile(p => ({...p, dependents: p.dependents.map((d, j) => j === i ? {...d, dob: e.target.value} : d)}))} /></div>
                      <div><label className="label">SSN</label><input className="input" type="password" value={dep.ssn || ''} onChange={e => setTaxProfile(p => ({...p, dependents: p.dependents.map((d, j) => j === i ? {...d, ssn: e.target.value} : d)}))} placeholder="XXX-XX-XXXX" /></div>
                    </div>
                  </div>
                ))}
                <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setTaxProfile(p => ({...p, dependents: [...(p.dependents || []), {name:'', ssn:'', dob:'', relationship:''}]}))}>+ Add Dependent</button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={handleSaveProfile} disabled={savingProfile}>
                  {savingProfile ? 'Saving…' : '💾 Save Tax Profile'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
